// Articles Service - CMS for curated content

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  Timestamp,
} from '@firebase/firestore';
import { db } from './firebase';
import { isUserAdmin } from './adminService';

const ARTICLES_COLLECTION = 'articles';

export type ArticleCategory = 'places' | 'goodlife' | 'goodwork' | 'inspiration' | 'links' | 'news' | 'bluezone';
export type ArticleStatus = 'draft' | 'published';

// Zielgruppen-Tags für Landingpage-Filterung
export type TargetAudience = 'all' | 'arztpraxen' | 'kitas' | 'gruendung' | 'karriere';

export const TARGET_AUDIENCES: { value: TargetAudience; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'arztpraxen', label: 'Arztpraxen' },
  { value: 'kitas', label: 'Kitas' },
  { value: 'gruendung', label: 'Gründung & Selbständigkeit' },
  { value: 'karriere', label: 'Karriere & Neuorientierung' },
];

export interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImage?: string;
  author: string;
  authorId: string;
  category: ArticleCategory;
  tags: string[];
  status: ArticleStatus;
  featured: boolean;
  publishedAt?: Timestamp | Date;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  // Externe Artikel (z.B. aus GoodWork News)
  isExternal?: boolean;
  externalUrl?: string;
  sourceType?: 'magazine' | 'news';
  // Bluezone Rezept-Verknuepfung
  linkedRecipeId?: string;
  // Zielgruppen-Tags für Landingpage-Filterung
  targetAudience: TargetAudience[];
}

export interface ArticleCreateInput {
  title: string;
  excerpt: string;
  content: string;
  coverImage?: string;
  category: ArticleCategory;
  tags?: string[];
  featured?: boolean;
  isExternal?: boolean;
  externalUrl?: string;
  linkedRecipeId?: string;
  targetAudience?: TargetAudience[];
}

export interface ArticleUpdateInput {
  title?: string;
  excerpt?: string;
  content?: string;
  coverImage?: string;
  category?: ArticleCategory;
  tags?: string[];
  featured?: boolean;
  status?: ArticleStatus;
  isExternal?: boolean;
  externalUrl?: string;
  linkedRecipeId?: string;
  targetAudience?: TargetAudience[];
}

/**
 * Generate a URL-friendly slug from title
 */
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[äÄ]/g, 'ae')
    .replace(/[öÖ]/g, 'oe')
    .replace(/[üÜ]/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 60);
}

/**
 * Create a new article (draft)
 */
export async function createArticle(
  adminId: string,
  authorName: string,
  input: ArticleCreateInput
): Promise<string> {
  // Verify admin status
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Artikel erstellen');
  }

  const slug = generateSlug(input.title);

  const docRef = await addDoc(collection(db, ARTICLES_COLLECTION), {
    title: input.title,
    slug,
    excerpt: input.excerpt,
    content: input.content,
    coverImage: input.coverImage || null,
    author: authorName,
    authorId: adminId,
    category: input.category,
    tags: input.tags || [],
    status: 'draft' as ArticleStatus,
    featured: input.featured || false,
    isExternal: input.isExternal || false,
    externalUrl: input.externalUrl || null,
    linkedRecipeId: input.linkedRecipeId || null,
    targetAudience: input.targetAudience || ['all'],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Update an article
 */
export async function updateArticle(
  adminId: string,
  articleId: string,
  input: ArticleUpdateInput
): Promise<void> {
  // Verify admin status
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Artikel bearbeiten');
  }

  // Filter out undefined values (Firestore doesn't accept undefined)
  const filteredInput = Object.fromEntries(
    Object.entries(input).filter(([_, value]) => value !== undefined)
  );

  const updates: any = {
    ...filteredInput,
    updatedAt: serverTimestamp(),
  };

  // Regenerate slug if title changed
  if (input.title) {
    updates.slug = generateSlug(input.title);
  }

  // Handle coverImage explicitly: if empty string, set to null (delete)
  if ('coverImage' in filteredInput && filteredInput.coverImage === '') {
    updates.coverImage = null;
  }

  await updateDoc(doc(db, ARTICLES_COLLECTION, articleId), updates);
}

/**
 * Create and optionally publish an article in one step
 * Used for quick-add functionality (News, Links, Tips)
 */
export async function createQuickArticle(
  adminId: string,
  authorName: string,
  input: ArticleCreateInput,
  publish: boolean = false
): Promise<string> {
  // Verify admin status
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Artikel erstellen');
  }

  const slug = generateSlug(input.title);

  const docRef = await addDoc(collection(db, ARTICLES_COLLECTION), {
    title: input.title,
    slug,
    excerpt: input.excerpt || '',
    content: input.content,
    coverImage: input.coverImage || null,
    author: authorName,
    authorId: adminId,
    category: input.category,
    tags: input.tags || [],
    status: publish ? ('published' as ArticleStatus) : ('draft' as ArticleStatus),
    featured: input.featured || false,
    isExternal: input.isExternal || false,
    externalUrl: input.externalUrl || null,
    linkedRecipeId: input.linkedRecipeId || null,
    targetAudience: input.targetAudience || ['all'],
    publishedAt: publish ? serverTimestamp() : null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Publish an article
 */
export async function publishArticle(
  adminId: string,
  articleId: string
): Promise<void> {
  // Verify admin status
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Artikel veroeffentlichen');
  }

  await updateDoc(doc(db, ARTICLES_COLLECTION, articleId), {
    status: 'published' as ArticleStatus,
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Unpublish an article (back to draft)
 */
export async function unpublishArticle(
  adminId: string,
  articleId: string
): Promise<void> {
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Artikel zurueckziehen');
  }

  await updateDoc(doc(db, ARTICLES_COLLECTION, articleId), {
    status: 'draft' as ArticleStatus,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete an article
 */
export async function deleteArticle(
  adminId: string,
  articleId: string
): Promise<void> {
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Artikel loeschen');
  }

  await deleteDoc(doc(db, ARTICLES_COLLECTION, articleId));
}

/**
 * Get a single article by ID
 */
export async function getArticleById(articleId: string): Promise<Article | null> {
  const docSnap = await getDoc(doc(db, ARTICLES_COLLECTION, articleId));
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as Article;
}

/**
 * Get a single article by slug
 */
export async function getArticleBySlug(slug: string): Promise<Article | null> {
  const q = query(
    collection(db, ARTICLES_COLLECTION),
    where('slug', '==', slug),
    where('status', '==', 'published'),
    limit(1)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as Article;
}

/**
 * Get all published articles
 */
export async function getPublishedArticles(
  category?: ArticleCategory,
  maxResults: number = 20,
  audience?: TargetAudience
): Promise<Article[]> {
  console.log('[ArticlesService] getPublishedArticles called, category:', category, 'audience:', audience);

  try {
    let q = query(
      collection(db, ARTICLES_COLLECTION),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
      limit(audience ? maxResults * 3 : maxResults)
    );

    if (category) {
      q = query(
        collection(db, ARTICLES_COLLECTION),
        where('status', '==', 'published'),
        where('category', '==', category),
        orderBy('publishedAt', 'desc'),
        limit(audience ? maxResults * 3 : maxResults)
      );
    }

    const snapshot = await getDocs(q);
    let articles = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as Article[];

    // Audience-Filter:
    // - Spezifische Audience: Zeige Artikel mit 'all' ODER der angefragten Audience
    // - Keine/all Audience: Zeige NUR Artikel die 'all' in ihren Tags haben
    if (audience && audience !== 'all') {
      articles = articles.filter(article => {
        const targetAudiences = article.targetAudience || ['all'];
        return targetAudiences.includes('all') || targetAudiences.includes(audience);
      });
    } else {
      articles = articles.filter(article => {
        const targetAudiences = article.targetAudience || ['all'];
        return targetAudiences.includes('all');
      });
    }

    articles = articles.slice(0, maxResults);

    console.log('[ArticlesService] getPublishedArticles result:', articles.length, 'docs');
    return articles;
  } catch (error) {
    console.error('[ArticlesService] getPublishedArticles error:', error);
    throw error;
  }
}

/**
 * Get featured articles
 * @param maxResults - Maximum number of results to return
 * @param audience - Optional target audience filter (returns 'all' + specified audience)
 */
export async function getFeaturedArticles(
  maxResults: number = 5,
  audience?: TargetAudience
): Promise<Article[]> {
  console.log('[ArticlesService] getFeaturedArticles called, audience:', audience);

  try {
    const q = query(
      collection(db, ARTICLES_COLLECTION),
      where('status', '==', 'published'),
      where('featured', '==', true),
      orderBy('publishedAt', 'desc'),
      limit(audience ? maxResults * 3 : maxResults) // Fetch more if filtering
    );

    const snapshot = await getDocs(q);
    console.log('[ArticlesService] getFeaturedArticles result:', snapshot.size, 'docs');
    let articles = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as Article[];

    // Filter by audience if specified
    // Artikel erscheint nur wenn: 'all' in seinen Tags ist ODER die angefragte Audience in seinen Tags ist
    if (audience && audience !== 'all') {
      articles = articles.filter(article => {
        const targetAudiences = article.targetAudience || ['all'];
        return targetAudiences.includes('all') || targetAudiences.includes(audience);
      });
    } else if (!audience || audience === 'all') {
      // "Alle"-Ansicht: Nur Artikel zeigen die 'all' in ihren Tags haben
      articles = articles.filter(article => {
        const targetAudiences = article.targetAudience || ['all'];
        return targetAudiences.includes('all');
      });
    }

    // Limit after filtering
    articles = articles.slice(0, maxResults);

    console.log('[ArticlesService] Featured articles:', articles);
    return articles;
  } catch (error) {
    console.error('[ArticlesService] getFeaturedArticles error:', error);
    throw error;
  }
}

/**
 * Get articles filtered by target audience
 * Returns articles with 'all' OR the specified audience
 * @param audience - Target audience to filter by
 * @param category - Optional category filter
 * @param maxResults - Maximum number of results
 */
export async function getArticlesByAudience(
  audience: TargetAudience,
  category?: ArticleCategory,
  maxResults: number = 20
): Promise<Article[]> {
  console.log('[ArticlesService] getArticlesByAudience called, audience:', audience, 'category:', category);

  try {
    // Firestore doesn't support OR queries across array-contains, so we fetch and filter
    let q = query(
      collection(db, ARTICLES_COLLECTION),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
      limit(maxResults * 3) // Fetch more to have enough after filtering
    );

    if (category) {
      q = query(
        collection(db, ARTICLES_COLLECTION),
        where('status', '==', 'published'),
        where('category', '==', category),
        orderBy('publishedAt', 'desc'),
        limit(maxResults * 3)
      );
    }

    const snapshot = await getDocs(q);
    let articles = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as Article[];

    // Filter by audience:
    // - Spezifische Audience: Zeige Artikel mit 'all' ODER der angefragten Audience
    // - 'all' Audience: Zeige NUR Artikel die 'all' in ihren Tags haben
    if (audience !== 'all') {
      articles = articles.filter(article => {
        const targetAudiences = article.targetAudience || ['all'];
        return targetAudiences.includes('all') || targetAudiences.includes(audience);
      });
    } else {
      articles = articles.filter(article => {
        const targetAudiences = article.targetAudience || ['all'];
        return targetAudiences.includes('all');
      });
    }

    // Limit after filtering
    articles = articles.slice(0, maxResults);

    console.log('[ArticlesService] getArticlesByAudience result:', articles.length, 'docs');
    return articles;
  } catch (error) {
    console.error('[ArticlesService] getArticlesByAudience error:', error);
    throw error;
  }
}

/**
 * Get all articles (for admin)
 */
export async function getAllArticles(): Promise<Article[]> {
  console.log('[ArticlesService] getAllArticles called');

  try {
    const q = query(
      collection(db, ARTICLES_COLLECTION),
      orderBy('updatedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    console.log('[ArticlesService] getAllArticles result:', snapshot.size, 'docs');
    const articles = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as Article[];
    console.log('[ArticlesService] All articles:', articles);
    return articles;
  } catch (error) {
    console.error('[ArticlesService] getAllArticles error:', error);
    throw error;
  }
}

/**
 * Subscribe to all articles (real-time, for admin)
 */
export function subscribeToArticles(
  callback: (articles: Article[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(
    collection(db, ARTICLES_COLLECTION),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const articles = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Article[];
      callback(articles);
    },
    (error) => {
      console.error('[ArticlesService] subscribeToArticles error:', error);
      if (onError) {
        onError(error);
      }
      // Return empty array on permission error to prevent crash
      callback([]);
    }
  );
}

/**
 * Subscribe to published articles (real-time, for public)
 */
export function subscribeToPublishedArticles(
  callback: (articles: Article[]) => void,
  maxResults: number = 10,
  onError?: (error: Error) => void,
  audience?: TargetAudience
): Unsubscribe {
  const q = query(
    collection(db, ARTICLES_COLLECTION),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(audience ? maxResults * 3 : maxResults)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      let articles = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Article[];

      // Audience-Filter: Ohne spezifische Audience nur 'all'-Artikel zeigen
      if (audience && audience !== 'all') {
        articles = articles.filter(article => {
          const targetAudiences = article.targetAudience || ['all'];
          return targetAudiences.includes('all') || targetAudiences.includes(audience);
        });
      } else {
        articles = articles.filter(article => {
          const targetAudiences = article.targetAudience || ['all'];
          return targetAudiences.includes('all');
        });
      }

      callback(articles.slice(0, maxResults));
    },
    (error) => {
      console.error('[ArticlesService] subscribeToPublishedArticles error:', error);
      if (onError) {
        onError(error);
      }
      // Return empty array on permission error to prevent crash
      callback([]);
    }
  );
}

/**
 * Category labels for UI - mit Lucide Icon-Namen
 */
export const ARTICLE_CATEGORIES: { value: ArticleCategory; label: string; icon: string }[] = [
  { value: 'places', label: 'Gute Orte', icon: 'MapPin' },
  { value: 'goodlife', label: 'GoodLife', icon: 'Heart' },
  { value: 'goodwork', label: 'GoodWork', icon: 'Briefcase' },
  { value: 'inspiration', label: 'Inspiration', icon: 'Sparkles' },
  { value: 'links', label: 'Links & Tipps', icon: 'ExternalLink' },
  { value: 'news', label: 'News', icon: 'Newspaper' },
  { value: 'bluezone', label: 'Bluezone Rezepte', icon: 'Utensils' },
];

/**
 * Legacy category mapping for migration
 */
export const LEGACY_CATEGORY_MAP: Record<string, ArticleCategory> = {
  'guide': 'goodlife',
  'story': 'inspiration',
};

/**
 * Migrate legacy categories to new categories (Admin only)
 * Call this once to update existing articles
 */
export async function migrateArticleCategories(adminId: string): Promise<{
  migrated: number;
  skipped: number;
  errors: string[];
}> {
  const isAdmin = await isUserAdmin(adminId);
  if (!isAdmin) {
    throw new Error('Nur Admins koennen Artikel migrieren');
  }

  const result = { migrated: 0, skipped: 0, errors: [] as string[] };

  try {
    const allArticles = await getAllArticles();

    for (const article of allArticles) {
      const legacyCategory = article.category as string;

      // Check if category needs migration
      if (LEGACY_CATEGORY_MAP[legacyCategory]) {
        const newCategory = LEGACY_CATEGORY_MAP[legacyCategory];

        try {
          await updateDoc(doc(db, ARTICLES_COLLECTION, article.id), {
            category: newCategory,
            updatedAt: serverTimestamp(),
          });
          result.migrated++;
          console.log(`[Migration] ${article.id}: ${legacyCategory} -> ${newCategory}`);
        } catch (error) {
          result.errors.push(`${article.id}: ${error}`);
        }
      } else {
        result.skipped++;
      }
    }

    console.log('[Migration] Complete:', result);
    return result;
  } catch (error) {
    console.error('[Migration] Error:', error);
    throw error;
  }
}
